import { createRng } from '@battle/sim';

/** Small deterministic value noise for CPU-side world generation (terrain, scatter). */
export function createNoise2D(seed: number) {
  const rng = createRng(seed);
  const size = 256;
  const perm = new Uint8Array(size * 2);
  const values = new Float32Array(size);
  for (let i = 0; i < size; i++) {
    perm[i] = i;
    values[i] = rng.next();
  }
  for (let i = size - 1; i > 0; i--) {
    const j = rng.int(0, i);
    const tmp = perm[i] as number;
    perm[i] = perm[j] as number;
    perm[j] = tmp;
  }
  for (let i = 0; i < size; i++) perm[i + size] = perm[i] as number;

  const lattice = (x: number, y: number) =>
    values[perm[(perm[x & 255] as number) + (y & 255)] as number] as number;
  const smooth = (t: number) => t * t * (3 - 2 * t);

  const noise = (x: number, y: number): number => {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const tx = smooth(x - xi);
    const ty = smooth(y - yi);
    const a = lattice(xi, yi);
    const b = lattice(xi + 1, yi);
    const c = lattice(xi, yi + 1);
    const d = lattice(xi + 1, yi + 1);
    return a + (b - a) * tx + (c - a) * ty + (a - b - c + d) * tx * ty;
  };

  /** Fractal sum in [0, 1]. */
  return (x: number, y: number, octaves = 4): number => {
    let sum = 0;
    let amp = 0.5;
    let freq = 1;
    let norm = 0;
    for (let o = 0; o < octaves; o++) {
      sum += noise(x * freq, y * freq) * amp;
      norm += amp;
      amp *= 0.5;
      freq *= 2;
    }
    return sum / norm;
  };
}
